import React, { createContext, useState, useEffect, useContext } from "react";
import { AuthContext } from "./AuthContext";
import axios from "axios";
import { connectSocket, getSocket } from "../socket";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user, token } = useContext(AuthContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3"); // Ensure this file exists in /public
    audio.play().catch((err) => console.warn("🔇 Failed to play sound:", err));
  };

  const showStyledToast = (notif) => {
    let icon = "🔔";
    let style = {};
    let title = "🔔 Notification";

    switch (notif.type) {
      case "system":
        icon = "🌤️";
        title = "🌤️ Solar Weather Alert";
        style = { background: "#f0fdf4", color: "#166534" };
        break;
      case "chat":
        icon = "💬";
        title = "💬 New Message";
        style = { background: "#eff6ff", color: "#1d4ed8" };
        break;
      case "booking":
        icon = "📅";
        title = "📅 Booking Update";
        style = { background: "#fefce8", color: "#92400e" };
        break;
      default:
        icon = "🔔";
        title = "🔔 Notification";
        style = { background: "#f3f4f6", color: "#111827" };
        break;
    }

    toast(
      <div>
        <strong>{title}</strong>
        <div>{notif.message}</div>
      </div>,
      {
        icon,
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        style,
      }
    );
  };

  const fetchUnread = async () => {
    if (!user || !token) return;
    try {
      const res = await axios.get(
        `https://globisun-app.onrender.com/api/notifications/unread-count/${user._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error("🔴 Failed to fetch unread count:", err.message);
    }
  };

  const fetchNotifications = async () => {
    if (!token || !user?._id) return;
    try {
      const res = await axios.get(
        `https://globisun-app.onrender.com/api/notifications/${user._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNotifications(res.data || []);
      await fetchUnread();
    } catch (err) {
      console.error("❌ Failed to fetch notifications:", err.message);
    }
  };

  // Mark all notifications as read, reset unread count
  const markAllAsRead = async () => {
    if (!token || !user?._id) return;
    try {
      await axios.patch(
        `https://globisun-app.onrender.com/api/notifications/mark-read/${user._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUnreadCount(0);
      await fetchNotifications();
    } catch (err) {
      console.error("❌ Failed to mark notifications as read:", err.message);
    }
  };

  // Poll unread count every 15 seconds
  useEffect(() => {
    if (!user || !token) return;

    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user, token]);

  // Socket.io setup for real-time notifications
  useEffect(() => {
    if (!user?._id) return;

    connectSocket(user._id);
    const socket = getSocket();

    const handleNotification = (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
      playNotificationSound();
      showStyledToast(notif);
    };

    socket.on("newNotification", handleNotification);

    return () => {
      socket.off("newNotification", handleNotification);
    };
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        notifications,
        fetchNotifications,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
